import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import FilterPanel, { type BasicFilters } from '../../components/FilterPanel';
import ResultsTable from '../../components/ResultsTable';
import FeatureIntro from '../../components/common/FeatureIntro';
import type { ProgramLockStatus, ProgramSearchResult, ProgramSearchResults } from '../../types/rpc-contracts';
import { useLovabletsawardError } from '../../hooks/useLovabletsawardData';
import PaginationControls from '../../components/common/PaginationControls';
import styles from './DataQualityView.module.css';
import { useLockedProgramsReport, lockedProgramsReportKey, programLockQueryKey } from '../../hooks/useProgramLock';
import { lockProgramData, unlockProgramData } from '../../services/rpc';
// import { useSeedSourcePrograms } from '../../hooks/useSeedSourcePrograms'; // Commented out - using hardcoded list

type PaginationState = {
  page: number;
  pageSize: number;
  totalCount: number;
  currentCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

const ynLabel = (value?: 'yes' | 'no' | 'all') => value === 'yes' ? 'Yes' : value === 'no' ? 'No' : 'All';

const ALLOWED_SORT_KEYS: ReadonlyArray<'name' | 'categories'> = ['name', 'categories'];

type SortKey = (typeof ALLOWED_SORT_KEYS)[number];

const mapSortKey = (value?: BasicFilters['sort_by']) => {
  if (!value) return undefined;
  return ALLOWED_SORT_KEYS.includes(value as SortKey)
    ? value as SortKey
    : undefined;
};

const VETTING_LABELS: Record<string, string> = {
  green: 'Approved',
  yellow: 'Review',
  red: 'Not suitable'
};

type ProgramWithExtras = ProgramSearchResult & {
  categories?: Array<unknown>;
  baci_program_code?: string | null;
};

export interface DataQualityViewProps {
  className?: string;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  committedSearch: string;
  onSearchSubmit: () => void;
  onSearchClear: () => void;
  filters: BasicFilters;
  onFiltersChange: (filters: BasicFilters) => void;
  pagination: PaginationState;
  results: ProgramSearchResults | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => Promise<ProgramSearchResults | undefined>;
  selectedProgramIds: Set<string>;
  setSelectedProgramIds: (value: Set<string>) => void;
  selectedSearchId: string | null;
  setSelectedSearchId: (value: string | null) => void;
  startPrintJob: (selectedIds: Set<string>) => Promise<string>;
  activeCategoryFilter: {
    categoryIds: string[];
    readerSegment: string | null;
    tier1: string | null;
    tier2: string | null;
  } | null;
  onClearCategoryFilter: () => void;
}

export const DataQualityView: React.FC<DataQualityViewProps> = ({
  className = '',
  searchTerm,
  onSearchTermChange,
  committedSearch,
  onSearchSubmit,
  onSearchClear,
  filters,
  onFiltersChange,
  pagination,
  results,
  isLoading,
  isFetching,
  error,
  refetch,
  selectedProgramIds,
  setSelectedProgramIds,
  selectedSearchId,
  setSelectedSearchId,
  startPrintJob,
  activeCategoryFilter,
  onClearCategoryFilter
}) => {
  const { getErrorMessage } = useLovabletsawardError();
  const queryClient = useQueryClient();
  const [lockingIds, setLockingIds] = React.useState<Set<string>>(new Set());
  const programs = React.useMemo(() => results?.programs ?? [], [results?.programs]);
  const totalCount = results?.total_count ?? 0;
  const pagedCategoryCount = React.useMemo(() => {
    return programs.reduce((sum, program) => {
      return sum + (program.category_count ?? 0);
    }, 0);
  }, [programs]);
  const categoryCount = results?.total_category_count ?? pagedCategoryCount;

  // Calculate total opportunities across all programs as a fallback when the RPC
  // doesn't provide aggregated data (older deployments).
  const pagedOpportunityCount = React.useMemo(() => {
    return programs.reduce((sum, program) => {
      // Only count programs that have explicit win_opportunities data
      // Don't fall back to category_count (that recreates the bug we just fixed)
      const programOpportunities = program.win_opportunities ?? 0;
      return sum + programOpportunities;
    }, 0);
  }, [programs]);
  const opportunityCount = results?.total_win_opportunities ?? pagedOpportunityCount;

  const lockedProgramsQuery = useLockedProgramsReport();
  const { refetch: refetchLockedPrograms } = lockedProgramsQuery;
  const hasVettingFilter = Array.isArray(filters.vetting_statuses) && filters.vetting_statuses.length > 0;
  const categoryFilterBadge = React.useMemo(() => {
    if (!activeCategoryFilter) {
      return null;
    }

    const parts: string[] = [];
    if (activeCategoryFilter.readerSegment) {
      parts.push(activeCategoryFilter.readerSegment);
    }
    if (activeCategoryFilter.tier1) {
      parts.push(activeCategoryFilter.tier1);
    }
    if (activeCategoryFilter.tier2) {
      parts.push(activeCategoryFilter.tier2);
    }

    const count = activeCategoryFilter.categoryIds.length;
    const label = parts.length > 0 ? parts.join(' › ') : `${count} selected`;
    return {
      label,
      count,
    } as const;
  }, [activeCategoryFilter]);
  const lockStatusMap = React.useMemo(() => {
    const map = new Map<string, ProgramLockStatus>();
    (lockedProgramsQuery.data ?? []).forEach((status) => {
      if (status?.program_id) {
        map.set(status.program_id, status);
      }
    });
    return map;
  }, [lockedProgramsQuery.data]);

  const setLockingState = React.useCallback((programId: string, locking: boolean) => {
    setLockingIds((prev) => {
      const next = new Set(prev);
      if (locking) {
        next.add(programId);
      } else {
        next.delete(programId);
      }
      return next;
    });
  }, []);

  const handleToggleLock = React.useCallback(async (program: ProgramSearchResult, currentlyLocked: boolean) => {
    const programId = program.id;
    setLockingState(programId, true);
    const reason = currentlyLocked ? 'Unlocked in Data Quality view' : 'Locked in Data Quality view';
    const metadata = { source: 'data-quality-view' };

    try {
      const response = currentlyLocked
        ? await unlockProgramData(programId, reason, null, metadata)
        : await lockProgramData(programId, reason, metadata, null);

      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Unknown lock operation failure');
      }

      const status = response.data;

      queryClient.setQueriesData<ProgramSearchResults | undefined>({ queryKey: ['program-search'] }, (existing) => {
        if (!existing || !Array.isArray(existing.programs)) {
          return existing;
        }

        let mutated = false;
        const updatedPrograms = existing.programs.map((item) => {
          if (item.id !== programId) {
            return item;
          }
          mutated = true;
          return { ...item, data_locked: status.data_locked };
        });

        if (!mutated) {
          return existing;
        }

        return {
          ...existing,
          programs: updatedPrograms
        } satisfies ProgramSearchResults;
      });

      queryClient.setQueryData<ProgramLockStatus[] | undefined>(lockedProgramsReportKey, (existing) => {
        const withoutCurrent = (existing ?? []).filter((item) => item.program_id !== status.program_id);
        if (status.data_locked) {
          return [status, ...withoutCurrent];
        }
        return withoutCurrent;
      });

      await queryClient.invalidateQueries({ queryKey: programLockQueryKey(programId) });
      void refetchLockedPrograms();
    } catch (toggleError) {
      console.error('Failed to toggle lock state', toggleError);
      alert(`Failed to ${currentlyLocked ? 'unlock' : 'lock'} ${program.name}. ${getErrorMessage(toggleError)}`);
    } finally {
      setLockingState(programId, false);
    }
  }, [getErrorMessage, queryClient, refetchLockedPrograms, setLockingState]);

  const handleSort = React.useCallback((key: SortKey) => {
    const nextDirection: 'asc' | 'desc' = (filters.sort_by === key && filters.sort_direction === 'asc') ? 'desc' : 'asc';
    onFiltersChange({ ...filters, sort_by: key, sort_direction: nextDirection });
  }, [filters, onFiltersChange]);

  const handlePrintResults = async () => {
    try {
      const jobId = await startPrintJob(selectedProgramIds);
      const url = `${window.location.origin}/#print-results?job_id=${encodeURIComponent(jobId)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to start print preview:', err);
      alert('Failed to start print preview. Please try again.');
    }
  };

  const renderResults = () => {
    if (error) {
      return (
        <div className={styles.errorAlert}>
          Failed to search programs: {getErrorMessage(error)}
        </div>
      );
    }

    if (isLoading) {
      return <div className={styles.loadingMessage}>Loading…</div>;
    }

    return (
      <div>
        <div className={styles.resultsHeader}>
          <div className={styles.resultsSummary}>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isLoading || isFetching}
              className={styles.resultsButton}
            >
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </button>
            <button onClick={handlePrintResults} className={styles.resultsButton}>
              Print results
            </button>
          </div>
        {totalCount > 0 && (
            <PaginationControls
              currentPage={pagination.page}
              pageSize={pagination.pageSize}
              totalCount={totalCount}
              currentCount={programs.length}
              onPageChange={pagination.onPageChange}
              onPageSizeChange={pagination.onPageSizeChange}
            />
          )}
        </div>

        <ResultsTable
          programs={programs}
          selectedProgramIds={selectedProgramIds}
          setSelectedProgramIds={setSelectedProgramIds}
          selectedSearchId={selectedSearchId}
          setSelectedSearchId={setSelectedSearchId}
          sort_by={mapSortKey(filters.sort_by)}
          sort_direction={filters.sort_direction}
          onSort={handleSort}
          searchTerm={committedSearch}
          lockStatusMap={lockStatusMap}
          onToggleLock={handleToggleLock}
          togglingLockIds={lockingIds}
          onOpenProgramCard={async (program) => {
            const extended = program as ProgramWithExtras;
            const codeFromRow = extended.baci_program_code ?? undefined;
            const url = codeFromRow
              ? `${window.location.origin}/#program-card?program_code=${encodeURIComponent(codeFromRow)}`
              : await (async () => {
                  const { getProgramCardUrlByProgramId } = await import('../../utils/programCard');
                  return await getProgramCardUrlByProgramId(program.id);
                })();
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
            } else {
              setSelectedSearchId(selectedSearchId === program.id ? null : program.id);
            }
          }}
        />

        {totalCount > 0 && (
          <div className={styles.paginationWrapper}>
            <PaginationControls
              currentPage={pagination.page}
              pageSize={pagination.pageSize}
              totalCount={totalCount}
              currentCount={programs.length}
              onPageChange={pagination.onPageChange}
              onPageSizeChange={pagination.onPageSizeChange}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.container} ${className}`.trim()}>
      <FeatureIntro eyebrow="Award Programs View" />

      <FilterPanel
        filters={filters}
        onFiltersChange={onFiltersChange}
        programCount={results?.total_count}
        categoryCount={categoryCount}
        opportunityCount={opportunityCount}
        isLoading={isLoading}
        className=""
      />

      <div className={styles.searchCard}>
        <div className={styles.searchRow}>
          <div className={styles.searchInputs}>
            <input
              type="text"
              id="data-quality-search-input"
              name="programSearch"
              placeholder="Search by program name, organization, or BACI code..."
              value={searchTerm}
              onChange={(event) => onSearchTermChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onSearchSubmit();
                }
              }}
              className={styles.searchInput}
            />
            <button
              aria-label="Search"
              onClick={onSearchSubmit}
              className={styles.searchButton}
            >
              Search
            </button>
          </div>
        </div>

        <div className={styles.filterTags}>
          {categoryFilterBadge && (
            <span className={`${styles.filterTag} ${styles.tagBlue}`}>
              Category: {categoryFilterBadge.label}
              <span style={{ color: '#64748b' }}>
                ({categoryFilterBadge.count} {categoryFilterBadge.count === 1 ? 'category' : 'categories'})
              </span>
              <button
                title="Clear category filter"
                onClick={onClearCategoryFilter}
                className={styles.tagButton}
              >
                ✕
              </button>
            </span>
          )}

          {committedSearch && (
            <span className={`${styles.filterTag} ${styles.tagPurple}`}>
              Search: “{committedSearch}”
              <button
                title="Clear search"
                onClick={onSearchClear}
                className={styles.tagButton}
              >
                ✕
              </button>
            </span>
          )}

          {hasVettingFilter && (
            <span className={`${styles.filterTag} ${styles.tagPurple}`}>
              Vetting: {filters.vetting_statuses?.map((status) => VETTING_LABELS[status] ?? status).join(', ')}
              <button
                title="Clear"
                onClick={() => onFiltersChange({ ...filters, vetting_statuses: undefined })}
                className={styles.tagButton}
              >
                ✕
              </button>
            </span>
          )}
          {(filters.isbn_required && filters.isbn_required !== 'all') && (
            <span title="Unspecified values are excluded" className={`${styles.filterTag} ${styles.tagPurple}`}>
              ISBN Required: {ynLabel(filters.isbn_required)} <span style={{ color: '#9ca3af' }}>ⓘ</span>
              <button title="Clear" onClick={() => onFiltersChange({ ...filters, isbn_required: undefined })} className={styles.tagButton}>✕</button>
            </span>
          )}
          {(filters.multiple_prizes_offered && filters.multiple_prizes_offered !== 'all') && (
            <span className={`${styles.filterTag} ${styles.tagPink}`}>
              Multiple Prizes: {ynLabel(filters.multiple_prizes_offered)}
              <button title="Clear" onClick={() => onFiltersChange({ ...filters, multiple_prizes_offered: undefined })} className={styles.tagButton}>✕</button>
            </span>
          )}
          {(filters.entry_fees && filters.entry_fees !== 'all') && (
            <span title="Unspecified values are excluded" className={`${styles.filterTag} ${styles.tagOrange}`}>
              Entry Fee: {filters.entry_fees === 'paid' ? 'Paid' : 'Free'} <span style={{ color: '#9ca3af' }}>ⓘ</span>
              <button title="Clear" onClick={() => onFiltersChange({ ...filters, entry_fees: undefined })} className={styles.tagButton}>✕</button>
            </span>
          )}
          {(filters.independent_publishers_allowed && filters.independent_publishers_allowed !== 'all') && (
            <span title="Unspecified values are excluded" className={`${styles.filterTag} ${styles.tagCyan}`}>
              Indie Allowed: {ynLabel(filters.independent_publishers_allowed)} <span style={{ color: '#9ca3af' }}>ⓘ</span>
              <button title="Clear" onClick={() => onFiltersChange({ ...filters, independent_publishers_allowed: undefined })} className={styles.tagButton}>✕</button>
            </span>
          )}
        </div>

        <div className={styles.sectionDivider}>
          {renderResults()}
        </div>
      </div>
    </div>
  );
};

export default DataQualityView;
